<script setup>
import { computed } from 'vue';
import DeskCollapse from './DeskCollapse.vue';

const props = defineProps({
    ticket: { type: Object, default: () => ({}) },
    toolbar: { type: Object, default: () => ({}) },
    labels: { type: Object, default: () => ({}) },
    ticketLabels: { type: Object, default: () => ({}) },
    permissions: { type: Object, default: () => ({}) },
    assignee: { type: [String, Number], default: '' },
    priority: { type: [String, Number], default: '' },
    mergeOpen: { type: Boolean, default: false },
    mergeTarget: { type: [String, Number], default: '' },
    mergeReason: { type: String, default: '' },
    currentUserId: { type: [String, Number], default: '' },
});

const emit = defineEmits([
    'update:assignee', 'update:priority', 'update:mergeOpen', 'update:mergeTarget',
    'update:mergeReason', 'save-operations', 'save-assignee', 'close-ticket', 'merge-ticket',
    'take-ownership', 'change-status',
]);

const optionValue = (option) => (option && typeof option === 'object' ? (option.value ?? option.id) : option);
const optionLabel = (option) => (option && typeof option === 'object' ? (option.label ?? option.name) : option);

const showTakeOwnership = () => {
    if (!props.permissions.assign) {
        return false;
    }
    if (props.assignee === '' || props.assignee === null || props.assignee === undefined) {
        return true;
    }
    return String(props.assignee) !== String(props.currentUserId);
};

const aiStatusBadgeClass = computed(() => {
    const status = props.ticket?.ai_status;
    if (status === 'failed') {
        return 'ticket-desk-badge--danger';
    }
    if (status === 'generating_draft' || status === 'pending') {
        return 'ticket-desk-badge--warning';
    }
    if (status === 'draft_generated' || status === 'draft_accepted') {
        return 'ticket-desk-badge--success';
    }

    return 'ticket-desk-badge--neutral';
});

const customerRatingBadgeClass = computed(() => {
    const variant = props.ticket?.customer_rating_badge;
    if (variant === 'success' || variant === 'danger' || variant === 'warning' || variant === 'neutral') {
        return `ticket-desk-badge--${variant}`;
    }

    return 'ticket-desk-badge--neutral';
});

// The payload already drops tier and label when the customer has no rating row.
const showCustomerRating = computed(() => Boolean(
    props.ticket?.customer_rating_tier && props.ticket?.customer_rating_tier_label,
));
const showAiMeta = computed(() => Boolean(props.ticket?.ai_status));
const showSentiment = computed(() => Boolean(props.ticket?.sentiment_label));

const showOperateActions = computed(() => (
    props.permissions.operate || props.permissions.merge || props.permissions.assign
));
</script>

<template>
    <header class="ticket-desk-toolbar" data-testid="ticket-desk-toolbar">
        <div class="ticket-desk-toolbar__top">
            <div class="ticket-desk-toolbar__identity">
                <div class="ticket-desk-toolbar__title-row">
                    <h1>#{{ ticket.id }} · {{ ticket.title }}</h1>
                    <span
                        class="ticket-desk-badge ticket-desk-badge--status"
                        :class="{ 'ticket-desk-badge--closed': ticket.is_open === false }"
                        data-testid="ticket-desk-status"
                    >{{ ticket.status_label || ticket.status }}</span>
                </div>
                <p class="ticket-desk-toolbar__requester">
                    <span>
                        {{ ticket.customer_name || ticket.customer?.name }}
                        <template v-if="ticket.customer_email || ticket.customer?.email"> · {{ ticket.customer_email || ticket.customer?.email }}</template>
                        <template v-if="ticket.channel_name"> · {{ ticket.channel_name }}</template>
                    </span>
                    <span
                        v-if="showCustomerRating"
                        class="ticket-desk-badge"
                        :class="customerRatingBadgeClass"
                        data-testid="ticket-desk-customer-rating"
                    >
                        {{ ticket.customer_rating_tier_label }}
                    </span>
                </p>
            </div>
            <div v-if="showOperateActions" class="ticket-desk-toolbar__actions">
                <button
                    v-if="showTakeOwnership()"
                    type="button"
                    class="ticket-desk-button ticket-desk-button--outline"
                    data-testid="ticket-desk-take-ownership"
                    @click="emit('take-ownership', { ticketId: ticket.id })"
                >
                    <i class="fa fa-user-plus" aria-hidden="true"></i>{{ labels.takeOwnership || labels.take_ownership || 'Prendi in carico' }}
                </button>
                <button
                    v-if="permissions.operate && toolbar.can_close !== false"
                    type="button"
                    class="ticket-desk-button ticket-desk-button--outline"
                    data-testid="ticket-desk-close"
                    @click="emit('close-ticket', { ticketId: ticket.id })"
                >
                    <span aria-hidden="true">✓</span>{{ labels.closeTicket || labels.close || 'Chiudi ticket' }}
                </button>
                <button
                    v-if="permissions.operate && toolbar.can_block"
                    type="button"
                    class="ticket-desk-button ticket-desk-button--outline"
                    data-testid="ticket-desk-block"
                    @click="emit('change-status', { ticketId: ticket.id, status: 'blocked' })"
                >
                    <span aria-hidden="true">⏸</span>{{ labels.blockTicket || 'Stand-by' }}
                </button>
                <button
                    v-if="permissions.operate && toolbar.can_unblock"
                    type="button"
                    class="ticket-desk-button ticket-desk-button--outline"
                    data-testid="ticket-desk-unblock"
                    @click="emit('change-status', { ticketId: ticket.id, status: 'in_progress' })"
                >
                    <span aria-hidden="true">▶</span>{{ labels.unblockTicket || 'Riprendi' }}
                </button>
                <button
                    v-if="permissions.merge && toolbar.can_merge"
                    type="button"
                    class="ticket-desk-button ticket-desk-button--outline"
                    :class="{ 'is-active': mergeOpen }"
                    :aria-expanded="mergeOpen"
                    aria-controls="ticket-desk-merge-panel"
                    data-testid="ticket-desk-merge"
                    @click="emit('update:mergeOpen', !mergeOpen)"
                >
                    <span aria-hidden="true">⇄</span>
                    {{ labels.mergeTicket || labels.merge || 'Unisci ticket' }}
                    <span class="ticket-desk-chevron" aria-hidden="true">▾</span>
                </button>
            </div>
        </div>

        <div class="ticket-desk-toolbar__fields">
            <div class="ticket-desk-toolbar__fields-left">
                <label v-if="permissions.assign" class="ticket-desk-field">
                    <span>{{ ticketLabels.assigned_user_id || ticketLabels.assignee || labels.assignee || 'Assegnatario' }}</span>
                    <select
                        :value="assignee"
                        data-testid="ticket-desk-assign"
                        @change="emit('update:assignee', $event.target.value); emit('save-assignee', { ticketId: ticket.id, assignee: $event.target.value })"
                    >
                        <option v-for="option in (toolbar.assignees || [])" :key="optionValue(option)" :value="optionValue(option)">
                            {{ optionLabel(option) }}
                        </option>
                    </select>
                </label>
                <label v-if="permissions.operate" class="ticket-desk-field">
                    <span>{{ ticketLabels.priority || labels.priority || 'Priorità' }}</span>
                    <select
                        :value="priority"
                        data-testid="ticket-desk-priority"
                        @change="emit('update:priority', $event.target.value); emit('save-operations', { ticketId: ticket.id, priority: $event.target.value })"
                    >
                        <option v-for="option in (toolbar.priorities || [])" :key="optionValue(option)" :value="optionValue(option)">
                            {{ optionLabel(option) }}
                        </option>
                    </select>
                </label>
            </div>
            <div v-if="showAiMeta || showSentiment" class="ticket-desk-toolbar__meta">
                <div v-if="showAiMeta" class="ticket-desk-toolbar__meta-item">
                    <span class="ticket-desk-toolbar__meta-label">{{ labels.aiDraftLabel || 'Bozza AI' }}:</span>
                    <span
                        class="ticket-desk-badge"
                        :class="aiStatusBadgeClass"
                        data-testid="ticket-desk-ai-status"
                    >
                        {{ ticket.ai_status_label || ticket.ai_status }}
                    </span>
                </div>
                <div v-if="showSentiment" class="ticket-desk-toolbar__meta-item">
                    <span class="ticket-desk-toolbar__meta-label">{{ labels.sentimentLabel || 'Sentiment' }}:</span>
                    <span class="ticket-desk-badge ticket-desk-badge--outline" data-testid="ticket-desk-sentiment">
                        {{ ticket.sentiment_label }}
                    </span>
                </div>
            </div>
        </div>

        <DeskCollapse v-if="permissions.merge && toolbar.can_merge" id="ticket-desk-merge-panel" :expanded="mergeOpen">
            <form
                class="ticket-desk-merge ticket-desk-merge--enter"
                @submit.prevent="emit('merge-ticket', { ticketId: ticket.id, targetTicketId: mergeTarget, reason: mergeReason })"
            >
                <select
                    :value="mergeTarget"
                    :aria-label="ticketLabels.mergeTarget || 'Ticket di destinazione'"
                    required
                    @change="emit('update:mergeTarget', $event.target.value)"
                >
                    <option value="" disabled>{{ labels.chooseTicket || 'Seleziona ticket' }}</option>
                    <option v-for="option in (toolbar.merge_targets || [])" :key="optionValue(option)" :value="optionValue(option)">
                        {{ optionLabel(option) }}
                    </option>
                </select>
                <input
                    :value="mergeReason"
                    :placeholder="ticketLabels.mergeReason || 'Motivo dell’unione'"
                    required
                    @input="emit('update:mergeReason', $event.target.value)"
                />
                <button type="submit" class="ticket-desk-button ticket-desk-button--primary">{{ labels.confirm || 'Conferma' }}</button>
            </form>
        </DeskCollapse>
    </header>
</template>
